/* A single responsive department navigation, enhanced from native details. */
(() => {
  // Shopify returns an accepted contact form's ID in the fragment but can drop
  // an alternate index view. Restore only that view, retaining the native result.
  try {
    const result = new URL(window.location.href);
    if (
      result.searchParams.getAll("contact_posted").length === 1 &&
      result.searchParams.get("contact_posted") === "true" &&
      result.hash.length < 512
    ) {
      const root = new URL(window.routes?.root_url || "/", result.origin);
      const normalizePath = (path) => path.replace(/\/$/, "") || "/";
      const match = decodeURIComponent(result.hash.slice(1)).match(
        /^(SaberQuote|RegionalServices|RepairPartsQuote|CreditAssistance)-[A-Za-z0-9_-]+--ss-view-([a-z-]+)$/,
      );
      const views = {
        SaberQuote: ["business", "contractor", "property-manager", "government"],
        RegionalServices: ["services"],
        RepairPartsQuote: ["repair-parts"],
        CreditAssistance: ["credit-application"],
      };
      if (
        root.origin === result.origin &&
        normalizePath(result.pathname) === normalizePath(root.pathname) &&
        match &&
        views[match[1]].includes(match[2])
      ) {
        if (!result.searchParams.has("view")) {
          result.searchParams.set("view", match[2]);
          window.location.replace(result.href);
          return;
        }
        if (
          result.searchParams.getAll("view").length === 1 &&
          result.searchParams.get("view") === match[2]
        ) {
          const form = document.getElementById(decodeURIComponent(result.hash.slice(1)));
          if (form?.matches("form")) {
            const status = form.querySelector('[role="status"][tabindex]');
            if (status) requestAnimationFrame(() => {
              if (!status.isConnected) return;
              status.focus({ preventScroll: true });
              status.scrollIntoView({ block: "center" });
            });
          }
        }
      }
    }
  } catch {
    // A malformed fragment leaves Shopify's original result page untouched.
  }
  if (window.saberNavigationInitialized) return;
  window.saberNavigationInitialized = true;
  document.documentElement.classList.add("ss-js");
  const desktop = window.matchMedia("(min-width: 750px)");
  const openMenu = () => document.querySelector(".ss-departments[open]");
  const observedPanels = new WeakSet();
  const observedMenus = new WeakSet();
  let menuFrame = 0;
  const schedulePanelHeight = () => {
    if (menuFrame) return;
    menuFrame = requestAnimationFrame(() => {
      menuFrame = 0;
      syncPanelHeight();
    });
  };
  const syncPanelHeight = () => {
    const menu = openMenu();
    if (!menu) return;
    const surface = menu.querySelector(".ss-mega");
    if (surface) {
      if (!observedMenus.has(surface)) {
        observedMenus.add(surface);
        surface.addEventListener("scroll", schedulePanelHeight, {
          passive: true,
        });
      }
      menu.style.setProperty(
        "--ss-menu-available-height",
        `${Math.max(0, Math.floor(window.innerHeight - surface.getBoundingClientRect().top - 12))}px`,
      );
    }
    const list = menu.querySelector(".ss-mega-department-list");
    const panel = list?.querySelector(
      ".ss-mega-department[open] > .ss-mega-panel",
    );
    if (!list) return;
    if (desktop.matches && panel) {
      list.style.setProperty(
        "--ss-menu-panel-height",
        `${Math.ceil(panel.getBoundingClientRect().height)}px`,
      );
      // Keep categories visible when a long department list scrolls. Capping
      // the offset inside the list prevents the panel extending its scroll area.
      const panelOffset = Math.max(
        0,
        Math.min(
          surface.getBoundingClientRect().top +
            12 -
            list.getBoundingClientRect().top,
          list.offsetHeight - panel.offsetHeight,
        ),
      );
      list.style.setProperty(
        "--ss-menu-panel-offset",
        `${Math.floor(panelOffset)}px`,
      );
      if (panelObserver && !observedPanels.has(panel)) {
        observedPanels.add(panel);
        panelObserver.observe(panel);
      }
    } else {
      list.style.removeProperty("--ss-menu-panel-height");
      list.style.removeProperty("--ss-menu-panel-offset");
    }
  };
  const panelObserver =
    typeof ResizeObserver === "function"
      ? new ResizeObserver(schedulePanelHeight)
      : null;
  const closeMenu = (restoreFocus = false) => {
    const menu = openMenu();
    if (!menu) return;
    menu
      .querySelector(".ss-mega-department-list")
      ?.style.removeProperty("--ss-menu-panel-offset");
    menu.open = false;
    if (restoreFocus) menu.querySelector(":scope > summary").focus();
  };
  document.addEventListener("click", (event) => {
    const menu = openMenu();
    if (event.target.closest("[data-ss-menu-close]")) {
      closeMenu(true);
      return;
    }
    if (menu && !menu.contains(event.target)) closeMenu();
  });
  document.addEventListener("focusin", (event) => {
    const menu = openMenu();
    if (menu && !menu.contains(event.target)) closeMenu();
  });
  document.addEventListener(
    "toggle",
    (event) => {
      const disclosure = event.target;
      if (disclosure.matches(".ss-departments")) {
        if (!disclosure.open) {
          disclosure
            .querySelector(".ss-mega-department-list")
            ?.style.removeProperty("--ss-menu-panel-offset");
          return;
        }
        // Select the first rendered, valid destination only on desktop.
        if (
          desktop.matches &&
          !disclosure.querySelector(".ss-mega-department[open]")
        ) {
          const first = disclosure.querySelector(".ss-mega-department");
          if (first) first.open = true;
        }
      } else if (disclosure.matches(".ss-mega-department, .ss-mega-category")) {
        if (disclosure.open) {
          const selector = disclosure.matches(".ss-mega-category")
            ? ":scope > .ss-mega-category[open]"
            : ":scope > .ss-mega-department[open]";
          disclosure.parentElement
            .querySelectorAll(selector)
            .forEach((sibling) => {
              if (sibling !== disclosure) sibling.open = false;
            });
        }
      } else return;
      schedulePanelHeight();
    },
    true,
  );
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeMenu(true);
      return;
    }
    const summary = event.target.closest(".ss-mega-department > summary");
    if (!summary || !desktop.matches) return;
    const department = summary.parentElement;
    const summaries = [
      ...department.parentElement.querySelectorAll(
        ":scope > .ss-mega-department > summary",
      ),
    ];
    const index = summaries.indexOf(summary);
    let next;
    if (event.key === "ArrowDown") next = (index + 1) % summaries.length;
    if (event.key === "ArrowUp")
      next = (index - 1 + summaries.length) % summaries.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = summaries.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      summaries[next].parentElement.open = true;
      summaries[next].focus();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      department.open = true;
      department.querySelector(".ss-mega-panel a")?.focus();
    }
  });
  window.addEventListener("resize", schedulePanelHeight, { passive: true });
  document.addEventListener("saber:header-state", () => {
    if (openMenu()) schedulePanelHeight();
  });
  desktop.addEventListener("change", () => {
    closeMenu();
    document.querySelectorAll(".ss-mega-department-list").forEach((list) => {
      list.style.removeProperty("--ss-menu-panel-height");
      list.style.removeProperty("--ss-menu-panel-offset");
    });
  });
})();
